import { createPopper } from '@popperjs/core';
import { ReactNode, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { restrictedZIndexes } from '../../utils';
import { Portal } from '../portal';

const Wrapper = styled.div<{ $minWidth?: number }>`
	pointer-events: all;
	${({ $minWidth }) =>
		$minWidth
			? css`
					min-width: ${$minWidth}px;
				`
			: ''}
	max-width: calc(100% - 30px);
	z-index: ${restrictedZIndexes.normal};
`;

interface PopoverProps {
	onClickOutside?: () => void;
	trigger: HTMLElement;
	children: ReactNode;
}

export function Popover({ onClickOutside, trigger, children }: PopoverProps): JSX.Element {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current || !trigger) {
			return;
		}

		const popper = createPopper(trigger, ref.current, {
			placement: 'bottom-start',
			strategy: 'fixed',
		});

		function handleOutsideClick(event: TouchEvent | MouseEvent): void {
			if (!ref.current?.contains(event.target as Node) && event.target !== trigger) {
				if (onClickOutside) {
					onClickOutside();
				}
			}
		}

		document.addEventListener('mousedown', handleOutsideClick);
		document.addEventListener('touchstart', handleOutsideClick);

		return () => {
			popper.destroy();

			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('touchstart', handleOutsideClick);
		};
	}, [onClickOutside, trigger]);

	return (
		<Portal>
			<Wrapper $minWidth={trigger.getBoundingClientRect().width} ref={ref}>
				{children}
			</Wrapper>
		</Portal>
	);
}

Popover.displayName = 'CellulaPopover';
